import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import DividerWithText from '@/components/common/DividerWithText';
import { FlexRow } from '@/components/common/Flex';
import LoadingWrapper from '@/components/common/LoadingWrapper';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Header, Paragraph, Text } from '@/components/common/Typography';
import usePlaid from '@/components/plaid/usePlaid';
import { toDollarsWithCents } from '@/util/currency';
import { TypedDocumentNode, useMutation } from '@apollo/client';
import { MutationHookOptions } from '@apollo/client/react/types/types';
import { WarningIcon } from '@chakra-ui/icons';
import { FlexProps, Spinner, VStack } from '@chakra-ui/react';
import {
    AnyDepositoryAccount,
    getCurrentValue,
} from '@common/financialEntities';
import { sortBy } from '@common/util/arrays';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../common/Button';
import { FlexCol } from '../common/Flex';
import { PlusCircle } from '../common/icons';
import AccountLockup from './AccountLockup';
import {
    useCheckDepositoryAccount,
    useSelectableDepositoryAccounts,
} from './helpers';

// TODO: Consider simplifying mutation props; we only use this in one place
interface DepositoryAccountSelectionViewProps<TData, TVariables>
    extends FlexProps {
    title: string;
    description: string;
    initialAccountId?: string;
    mutation: TypedDocumentNode<TData, TVariables>;
    mutationOptions?: MutationHookOptions<TData, TVariables>;
    getVariables: (accountId: string) => TVariables;
    onComplete: (accountId: string) => void;
    onSkip?: () => void;
}

export default function DepositoryAccountSelectionView<TData, TVariables>({
    title,
    description,
    initialAccountId,
    mutation,
    getVariables,
    onComplete,
    onSkip,
    mutationOptions,
    ...props
}: DepositoryAccountSelectionViewProps<TData, TVariables>) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState<
        string | undefined
    >(initialAccountId);
    const { errorMessageByAccountId, handleSelectedAccountError } =
        useCheckDepositoryAccount({ setSelectedAccountId });
    const [mutate] = useMutation(mutation, mutationOptions);

    return (
        <FlexCol {...props}>
            <Header mb={4}>{title}</Header>
            <Paragraph color="textMuted" mb={10}>
                {description}
            </Paragraph>
            <DepositoryAccountSelector
                selectedAccountId={selectedAccountId}
                onChangeSelectedAccountId={setSelectedAccountId}
                errorMessageByAccountId={errorMessageByAccountId}
                isDisabled={isSubmitting}
                grow={1}
                mb={10}
            />
            {onSkip && (
                <Button
                    variant="ghost"
                    colorScheme="gray"
                    onClick={onSkip}
                    onClickEvent="Skip Depository Account Selection Button Clicked"
                    mb={2}
                >
                    Skip
                </Button>
            )}
            <Button
                isDisabled={!selectedAccountId || isSubmitting}
                isLoading={isSubmitting}
                onClick={() => {
                    if (!selectedAccountId) return;
                    setIsSubmitting(true);
                    // N.B. We can't reuse checkDepositoryAccount here like we do
                    // elsewhere because we're calling updateTransactionSchedule
                    // instead of createAtomicExternalAccount.
                    mutate({
                        variables: getVariables(selectedAccountId),
                        onCompleted: () => {
                            onComplete(selectedAccountId);
                        },
                        onError: (e) => {
                            handleSelectedAccountError(selectedAccountId, e);
                            setSelectedAccountId(undefined);
                        },
                    }).finally(() => setIsSubmitting(false));
                }}
                onClickEvent="Choose Depository Account Button Clicked"
            >
                Use account
            </Button>
        </FlexCol>
    );
}

export interface DepositoryAccountSelectorProps extends FlexProps {
    selectedAccountId: string | undefined;
    onChangeSelectedAccountId: (selectedAccountId: string | undefined) => void;
    errorMessageByAccountId?: Record<string, string>;
    preselectFirstAccount?: boolean;
    isDisabled?: boolean;
}

export function DepositoryAccountSelector({
    selectedAccountId,
    onChangeSelectedAccountId,
    errorMessageByAccountId = {},
    preselectFirstAccount = true,
    isDisabled,
    ...props
}: DepositoryAccountSelectorProps) {
    const { open } = usePlaid();
    const { accounts, loading, error } = useSelectableDepositoryAccounts();
    const [hasExecutedPreselectLogic, setHasExecutedPreselectLogic] =
        useState(false);

    useEffect(() => {
        // Exit early if there are no accounts or we've already executed logic
        if (accounts.length === 0 || hasExecutedPreselectLogic) return;

        // If no account is selected, execute preselect logic
        if (!selectedAccountId) {
            const preselectAccountId = getPreselectAccountId(accounts);
            if (preselectAccountId) {
                onChangeSelectedAccountId(preselectAccountId);
            }
            setHasExecutedPreselectLogic(true);
        }
    }, [
        accounts,
        preselectFirstAccount,
        onChangeSelectedAccountId,
        hasExecutedPreselectLogic,
        selectedAccountId,
    ]);

    return (
        <LoadingWrapper isLoading={loading} error={error}>
            <FlexCol {...props}>
                <VStack spacing={4} align="stretch">
                    {accounts.map((da) => (
                        <DepositoryAccountItem
                            key={da.id}
                            account={da}
                            isSelected={da.id === selectedAccountId}
                            errorMessage={errorMessageByAccountId[da.id]}
                            onChange={onChangeSelectedAccountId}
                            isDisabled={isDisabled}
                        />
                    ))}
                </VStack>
                {!!accounts.length && <DividerWithText text="or" my={6} />}
                <ButtonWithIcon
                    variant="outline"
                    iconRight={<PlusCircle />}
                    onClick={open}
                    onClickEvent="Add Depository Account Button Clicked"
                >
                    Add account
                </ButtonWithIcon>
            </FlexCol>
        </LoadingWrapper>
    );
}

interface DepositoryAccountItemProps {
    account: AnyDepositoryAccount;
    isSelected: boolean;
    isDisabled?: boolean;
    errorMessage?: string;
    onChange: (selectedAccountId: string | undefined) => void;
}

export function DepositoryAccountItem({
    account,
    isSelected,
    isDisabled,
    errorMessage,
    onChange,
}: DepositoryAccountItemProps) {
    const handleClick = useCallback(() => {
        if (isDisabled) return;
        if (errorMessage) return;
        onChange(isSelected ? undefined : account.id);
    }, [isDisabled, errorMessage, onChange, isSelected, account.id]);
    const value = getCurrentValue(account);

    return (
        <FlexCol
            p={4}
            border="1px solid"
            borderColor={isSelected ? 'blue.400' : 'border'}
            bg={isSelected ? 'blue.50' : 'white'}
            rounded="md"
            {...hoverStyleProps({ bg: isSelected ? 'blue.50' : 'gray.50' })}
            _active={{
                bg: 'blue.50',
                borderColor: 'blue.400',
            }}
            opacity={isDisabled || errorMessage ? 0.5 : 1}
            pointerEvents={isDisabled || errorMessage ? 'none' : 'initial'}
            onClick={handleClick}
        >
            <FlexRow justify="space-between" align="center">
                <AccountLockup account={account} pr={4} />
                {value === null ? (
                    <Spinner size="sm" color="gray.500" />
                ) : (
                    <Text fontWeight="semibold">
                        {toDollarsWithCents(value)}
                    </Text>
                )}
            </FlexRow>
            {errorMessage && (
                <FlexRow align="center" pt={4}>
                    <WarningIcon mr={4} w={4} h={4} color="red.500" />
                    <Text>{errorMessage}</Text>
                </FlexRow>
            )}
        </FlexCol>
    );
}

const getPreselectAccountId = (
    accounts: AnyDepositoryAccount[]
): string | undefined => {
    // If only 1 account: auto select -> 1 account
    // If multi account: auto select -> checking account
    // If multi account: auto select -> checking account with the highest balance
    return sortBy(
        accounts,
        (account) =>
            (account.type === 'CheckingAccount' ? 1_000_000_000_00 : 0) +
            (account.latestSnapshot?.balance ?? 0),
        'DESC'
    ).at(0)?.id;
};
